// **The practice sheet** — the two surfaces the practice affordance opens (`client-practice`,
// `client-home`; sketch `device-and-resume.html`).
//
// It used to have four. **`home-and-single-player` deletes the other two**, and the reason is the
// same for both: neither had an action left worth a screen.
//
//   * **configuring** is gone because there is nothing left to configure. The rival count is fixed at
//     three and the seed comes from server configuration, so its only remaining control was a
//     "continue" — an intermediate sheet whose one action is to proceed. The CTA now enters the
//     sandbox directly.
//   * **resumed** ("Welcome back") is gone because its content was a recent-runs list, and the script
//     selector is already that list crossed with the versions that produced it. Two histories of one
//     thing is worse than one.
//
// What survives is exactly the two surfaces that carry a **refusal** — the states where entering the
// sandbox would be a lie:
//
//   * **refused** — the device failed the capability gate. Rendered as a **limitation, not an
//     error**: no `--danger`, no "failed", a per-capability checklist naming which check did not
//     pass, and a statement of what still works. "Your browser can't" is unfalsifiable and
//     unreportable, and naming the failed check is also what makes **D18**'s measured-device policy
//     visible to the person it affects. **No control that would start a run exists on this surface
//     at all** — not disabled, absent.
//   * **expired** — the session was cleared by retention. It says so, and when. It is emphatically
//     **not** an empty new session: the only action offered is an explicit start-over.
//
// Presentational throughout: every action is a callback, and the surface it renders is chosen by the
// composition root's typed outcome rather than by anything decided here.

import type { CSSProperties } from 'react';
import { Button } from '../components/core/Button';
import {
  PRACTICE_CAPABILITIES,
  practiceCapabilityCopy,
  type PracticeCapabilityReport,
} from '../../app/rounds/practice-capability';
import { expiredLine } from '../rounds/practice-retention';

/** Which of the two surfaces is up. Chosen by the root's typed outcome, never inferred. */
export type PracticeSurface =
  | { kind: 'refused'; report: PracticeCapabilityReport }
  | { kind: 'expired'; expiredAt: number; retentionDays: number };

export interface PracticeSheetProps {
  surface: PracticeSurface;
  /** The instant every "how long ago" phrase is measured against. A number, not a clock seam:
   * Home is presentational and this is the one value it cannot derive without reading a clock. */
  now: number;
  /** An action is in flight. Controls stay mounted and go quiet rather than disappearing. */
  busy?: boolean;
  onClose(): void;
  onStartOver(): void;
}

export function PracticeSheet({
  surface,
  now,
  busy = false,
  onClose,
  onStartOver,
}: PracticeSheetProps) {
  return (
    <div data-testid="practice-scrim" style={SCRIM_STYLE}>
      <div data-testid="practice-sheet" data-surface={surface.kind} style={SHEET_STYLE}>
        <button
          data-testid="practice-close"
          aria-label="Close"
          style={CLOSE_STYLE}
          onClick={onClose}
        >
          ✕
        </button>

        {surface.kind === 'refused' && <Refused report={surface.report} onClose={onClose} />}
        {surface.kind === 'expired' && (
          <Expired
            expiredAt={surface.expiredAt}
            retentionDays={surface.retentionDays}
            now={now}
            busy={busy}
            onStartOver={onStartOver}
          />
        )}

      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// refused
// ---------------------------------------------------------------------------------------

function Refused({
  report,
  onClose,
}: {
  report: PracticeCapabilityReport;
  onClose(): void;
}) {
  const ok = new Set(report.checks.filter((check) => check.ok).map((check) => check.name));
  return (
    <div data-testid="practice-refusal">
      <h2 style={TITLE_STYLE}>Practice needs a bit more from this browser</h2>
      <p style={BODY_STYLE}>
        Practice runs the whole simulation on your own device, so it asks for a little more than
        watching a tournament does.
      </p>

      <div style={PANEL_STYLE}>
        <span style={PANEL_TITLE_STYLE}>What this browser offers</span>
        <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0, display: 'grid', gap: 8 }}>
          {PRACTICE_CAPABILITIES.map((name) => {
            const passed = ok.has(name);
            const copy = practiceCapabilityCopy(name);
            return (
              <li
                key={name}
                data-testid={`practice-capability-${name}`}
                data-ok={passed ? 'true' : 'false'}
                style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 12.5, lineHeight: 1.5 }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    fontFamily: 'var(--font-mono)',
                    width: 14,
                    flex: 'none',
                    color: passed ? 'var(--success)' : 'var(--danger)',
                  }}
                >
                  {passed ? '✓' : '✕'}
                </span>
                <span style={{ color: 'var(--ink-2)' }}>
                  <b style={{ color: 'var(--ink-1)', fontWeight: 600 }}>{copy.label}</b>
                  {/* The reason rides only on the check that failed: naming *which* capability is
                      missing is the requirement, and repeating a justification beside every
                      passing row would bury it. */}
                  {!passed && <span> — {copy.need}</span>}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Required by `client-practice`: the refusal states what remains available, so it reads as a
          limited device rather than a broken application. */}
      <div data-testid="practice-refusal-still-works" style={PANEL_STYLE}>
        <span style={PANEL_TITLE_STYLE}>What still works</span>
        <p style={{ ...BODY_STYLE, marginTop: 6 }}>
          Joining a tournament, editing your scripts and watching graded rounds all work here as
          normal. It’s only practice runs that need to store a season on this device.
        </p>
      </div>

      {/* The way back. There is deliberately no start control anywhere on this surface — not a
          disabled one, none: a run this device cannot complete must not be offered. */}
      <Button variant="secondary" size="lg" data-testid="practice-refusal-back" onClick={onClose}>
        Back to home
      </Button>
      <p style={FOOT_STYLE}>
        Practice is tested on desktop Chrome, Edge and Firefox, and on Android. Try one of those, or
        open Waggle on a computer.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// expired
// ---------------------------------------------------------------------------------------

function Expired({
  expiredAt,
  retentionDays,
  now,
  busy,
  onStartOver,
}: {
  expiredAt: number;
  retentionDays: number;
  now: number;
  busy: boolean;
  onStartOver(): void;
}) {
  return (
    <div data-testid="practice-expired">
      <h2 style={TITLE_STYLE}>That practice colony expired</h2>
      {/* Named as expired, with when. The alternative — quietly opening a fresh session — is the
          silent-loss outcome the typed outcomes exist to prevent. */}
      <p data-testid="practice-expired-detail" style={BODY_STYLE}>
        {expiredLine(expiredAt, now, retentionDays)}
      </p>
      {/* **The trace bytes are what expire, not the scripts.** Device storage quota is a real bound
          and staged seasons are large; a player's scripts are neither, and telling them their work
          is trapped on this device would now be false — the sandbox is keyed to who they are. */}
      <p style={{ ...BODY_STYLE, marginTop: 8 }}>
        The seasons this colony had already simulated are gone with it. Starting a new one begins from
        the shipped preset colony.
      </p>
      <Button
        size="lg"
        data-testid="practice-start-over"
        disabled={busy}
        onClick={onStartOver}
        style={{ marginTop: 16, width: '100%' }}
      >
        Start a new practice colony
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// styles — the kit's tokens, the same set Home's own sheets use
// ---------------------------------------------------------------------------------------

const SCRIM_STYLE: CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 20,
  background: 'rgba(43,33,20,0.42)',
  overflowY: 'auto',
};

const SHEET_STYLE: CSSProperties = {
  position: 'relative',
  width: '100%',
  maxWidth: 390,
  background: 'var(--surface-card)',
  border: '1px solid var(--hud-line)',
  borderRadius: 'var(--r-lg)',
  boxShadow: 'var(--shadow-hud)',
  padding: 20,
};

const CLOSE_STYLE: CSSProperties = {
  position: 'absolute',
  top: 8,
  right: 8,
  width: 44,
  height: 44,
  borderRadius: 22,
  border: 'none',
  background: 'transparent',
  color: 'var(--ink-2)',
  fontSize: 19,
  cursor: 'pointer',
};

const TITLE_STYLE: CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  fontSize: 21,
  color: 'var(--ink-1)',
  margin: '0 26px 8px 0',
  lineHeight: 1.2,
};

const BODY_STYLE: CSSProperties = {
  fontSize: 13,
  color: 'var(--ink-2)',
  lineHeight: 1.55,
  margin: 0,
};

const PANEL_STYLE: CSSProperties = {
  marginTop: 14,
  padding: 13,
  borderRadius: 'var(--r-md)',
  border: '1px solid var(--line)',
  background: 'var(--paper-1)',
};

const PANEL_TITLE_STYLE: CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  fontSize: 13,
  color: 'var(--ink-1)',
};

const FOOT_STYLE: CSSProperties = {
  margin: '12px 0 0',
  fontSize: 11,
  color: 'var(--ink-2)',
  lineHeight: 1.5,
  textAlign: 'center',
};
